import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid2';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import {
  useZupAccessChange,
  useGetCandidateDetails,
} from 'src/pages/CandidatesDetailsPage/api';
import { useGetCandidate } from 'src/pages/CandidatesPage/api';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { CandidateTabProps } from 'src/pages/CandidatesDetailsPage/components/CandidateShiftsTab';
import CandidateInfo from 'src/pages/CandidatesDetailsPage/components/CandidateInfo/CandidateInfo';

import messages from './messages';

export default function CandidateAppSettingsTab({ uuid }: CandidateTabProps) {
  const { formatMessage } = useIntl();

  const { data: candidate } = useGetCandidateDetails({
    uuid,
    enabled: false,
  });
  const { data: candidateProfile } = useGetCandidate({
    uuid,
    enabled: Boolean(uuid),
  });
  const { mutate: zupAccessChange } = useZupAccessChange();

  const notification = useMemo(() => {
    if (!candidate?.data?.notification)
      return {
        essential: '-',
        standard: '-',
        priority: '-',
      };

    return {
      essential: candidate?.data?.notification?.standardJobUpdates
        ? formatMessage(messages.on)
        : formatMessage(messages.off),
      standard: candidate?.data?.notification?.standardJobUpdates
        ? formatMessage(messages.on)
        : formatMessage(messages.off),
      priority: candidate?.data?.notification?.priorityJobs
        ? formatMessage(messages.on)
        : formatMessage(messages.off),
    };
  }, [candidate?.data?.notification]);

  const handleZupAccessChange = useCallback(() => {
    if (!uuid || !candidateProfile) return;

    zupAccessChange({
      uuid,
      access: !candidateProfile.zupAccess,
    });
  }, [uuid, candidateProfile?.zupAccess]);

  if (!candidate?.data || !candidateProfile) return null;

  const { zupAccess } = candidateProfile;
  const { appLanguage, device } = candidate.data;

  return (
    <Grid container spacing={6} p={2}>
      <Grid
        container
        spacing={4}
        flexDirection="column"
        size={{ xs: 12, md: 4 }}
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.mobileApp)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.language)}
          value={appLanguage?.name}
        />
        <CandidateInfo
          label={formatMessage(messages.appVersion)}
          value={device?.appVersion}
        />
        <CandidateInfo
          label={formatMessage(messages.operationSystem)}
          value={device?.deviceOS}
          capitalize={false}
        />
        <CandidateInfo
          label={formatMessage(messages.zupAccess)}
          value={
            <ConfirmationDialog
              onConfirm={handleZupAccessChange}
              message={`${formatMessage(
                zupAccess
                  ? messages.cancelZupAccessMessage
                  : messages.confirmZupAccessMessage,
                {
                  name: `${candidateProfile.firstName} ${candidateProfile.lastName}`,
                },
              )}`}
            >
              {(handleOpen) => (
                <Switch onClick={handleOpen} checked={zupAccess} />
              )}
            </ConfirmationDialog>
          }
        />
      </Grid>
      <Grid
        container
        spacing={4}
        flexDirection="column"
        size={{ xs: 12, md: 4 }}
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.notifications)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.essentialUpdates)}
          value={notification.essential}
        />
        <CandidateInfo
          label={formatMessage(messages.standardJobUpdates)}
          value={notification.standard}
        />
        <CandidateInfo
          label={formatMessage(messages.priorityJobsUpdates)}
          value={notification.priority}
        />
      </Grid>
    </Grid>
  );
}
